import React, {useState, useEffect, useRef} from 'react'
import MaterialTable, { MTableAction, MTableToolbar } from 'material-table'
import { compareTwoObjs, validateFn, getItemsObj, getValidationFunction } from '../../../services/lib/utils/helpers'
import CommonButton from '../others/common-button'
import { Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'

// Jadcup品牌色系
const useStyles = makeStyles({
  tableContainer: {
    width: '100%',
    margin: 0,
    padding: 0,
    // 小屏幕上添加少量边距，大屏幕上紧贴屏幕
    '@media (max-width: 768px)': {
      padding: '0 8px',
    },
    // 强制显示滚动条轨道，避免抖动
    '& .MuiTableContainer-root': {
      overflowX: 'auto',
      overflowY: 'auto',
      scrollbarGutter: 'stable', // 现代浏览器预留滚动条空间
      // 自定义滚动条样式
      '&::-webkit-scrollbar': {
        height: '8px',
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f8e9',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#8bc34a',
        borderRadius: '4px',
        '&:hover': {
          background: '#7cb342',
        },
      },
    },
    '& .MuiPaper-root': {
      border: '1px solid #e8f5e9',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(139, 195, 74, 0.08)',
      // overflow: 'auto',
    },
    '& .MuiToolbar-root': {
      background: 'linear-gradient(135deg, #dcecc3 0%, #f1f8e9 100%)',
      borderBottom: '2px solid #689f38',
      padding: '16px 24px',
      '& button': {
        color: '#1A3C34',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#8bc34a',
          color: '#ffffff',
          // transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(139, 195, 74, 0.3)',
        },
      },
    },
    '& .MuiTableHead-root': {
      background: 'linear-gradient(180deg, #8bc34a 0%, #7cb342 100%)',
      '& .MuiTableCell-head': {
        color: '#1A3C34',
        fontWeight: 600,
        fontSize: '0.95rem',
        borderBottom: 'none',
        padding: '16px',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        transition: 'all 0.2s ease',
        // 只在非编辑状态下启用 hover table row 效果
        '&:hover': {
          backgroundColor: '#DDEED2',
          // transform: 'scale(1.001)',
        },
      },
      // 编辑行禁用所有 hover 效果（关键修复）
      '& tr[mode="update"], & tr[mode="add"]': {
        backgroundColor: 'transparent !important',
        transform: 'none !important',
        '&:hover': {
          backgroundColor: 'transparent !important',
          transform: 'none !important',
        },
        '& .MuiTableCell-root': {
          backgroundColor: 'transparent !important',
        },
      },
      '& .MuiTableCell-root': {
        borderBottom: '1px solid #e8f5e9',
        padding: '14px 16px',
      },
    },
    '& .MuiTablePagination-root': {
      borderTop: '1px solid #e8f5e9',
      background: '#fafafa',
    },
    '& .MuiIconButton-root': {
      color: '#1A3C34',
      transition: 'all 0.2s ease',
      borderRadius: '8px',
      padding: '8px',
      '&:hover': {
        backgroundColor: '#8bc34a',
        color: '#ffffff',
        // transform: 'scale(1.05)',
      },
    },
    '& .MuiTableCell-root .MuiIconButton-root': {
      margin: '0 4px',
      background: '#f1f8e9',
      '&:hover': {
        background: '#8bc34a',
        color: '#2b0707ff',
        boxShadow: '0 2px 8px rgba(139, 195, 74, 0.3)',
      },
    },
    '& .MuiCheckbox-root': {
      color: '#8bc34a',
      '&.Mui-checked': {
        color: '#689f38',
      },
    },
    '& .MuiTableSortLabel-root': {
      color: '#1A3C34',
      '&:hover': {
        color: '#0d261f',
      },
      '&.MuiTableSortLabel-active': {
        color: '#1A3C34',
      },
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#c5e1a5',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#8bc34a',
    },
    '& .MuiInputBase-input': {
      color: '#558b2f',
    },
    // 修复下拉列表样式
    '& .MuiSelect-root': {
      minWidth: '100px',
    },
    '& .MuiTableCell-root .MuiSelect-select': {
      paddingRight: '24px',
    },
  },
  headerFields: {
    display: 'flex',
    gap: '16px',
    padding: '12px 24px',
    background: '#ffffff',
    borderTop: '1px solid #e8f5e9',
    '& > div': {
      padding: '8px 16px',
      borderRadius: '20px',
      background: '#f1f8e9',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#558b2f',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        background: '#8bc34a',
        color: '#ffffff',
        // transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(139, 195, 74, 0.2)',
      },
      '& a': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
  },
  // 全局下拉菜单样式（防止抖动）
  '@global': {
    '.MuiMenu-paper': {
      border: '1px solid #e8f5e9 !important',
      borderRadius: '8px !important',
      boxShadow: '0 4px 12px rgba(139, 195, 74, 0.15) !important',
      maxHeight: '300px !important',
      // 关键：提高 z-index，确保菜单在最上层，不触发下方元素的 hover
      zIndex: '9999 !important',
      // 防止菜单影响下方表格行
      pointerEvents: 'auto !important',
      // 防止菜单内容抖动
      '& .MuiMenu-list': {
        padding: '4px !important',
        // 强制显示滚动条
        overflowY: 'auto !important',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f8e9',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#8bc34a',
          borderRadius: '4px',
          '&:hover': {
            background: '#7cb342',
          },
        },
      },
      '& .MuiMenuItem-root': {
        fontSize: '0.9rem !important',
        padding: '8px 16px !important',
        margin: '2px 0 !important',
        borderRadius: '6px !important',
        transition: 'all 0.2s ease !important',
        '&:hover': {
          backgroundColor: '#f1f8e9 !important',
          // transform: 'translateX(4px) !important',
        },
        '&.Mui-selected': {
          backgroundColor: '#e8f5e9 !important',
          color: '#558b2f !important',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: '#dcedc8 !important',
          },
        },
      },
    },
    // 修复 Popover 定位抖动
    '.MuiPopover-root': {
      // 提高 z-index，确保在最上层
      zIndex: '9999 !important',
      '& .MuiPaper-root': {
        // transformOrigin: 'top left !important',
      },
    },
    // 当下拉菜单打开时，添加遮罩层防止触发表格行 hover
    '.MuiBackdrop-root': {
      backgroundColor: 'transparent !important',
      pointerEvents: 'none !important',
    },
  },
})

export interface CommonTablePropsInterface {
  title?: string
  column: any
  initData: any
  editable?: boolean
  updateData?: any
  isNotDeletable?: boolean
  isNotEditable?: boolean
  isNotAddable?: boolean
  isExportable?: boolean
  actionButtons?: any
  addRowButtonName?: string
  notAllowUpdate?: boolean
  setTableData?: any
  headerFields?: any
  isEnableSelect?: any
  columnsButton?: any
  onSelectionChange?: any
  onFilterChange?: any
  onSearchChange?:any
  defaultPageSize?: number
  defaultPageSizeOptions?: any
  selectionProps?: any
  rowStyle?:any
  triggerData?:any
  disableSorting?: boolean // 是否禁用排序功能
  tableRef?: any // 允许外部传入 ref 来访问 tableRef
  onAddClick?: any // 覆盖内置 Add 按钮的点击行为（不传则保持默认的行内新增）
}

export default function CommonTable(props: CommonTablePropsInterface) {
    const classes = useStyles()
  // 创建内部 ref（总是调用，不违反 Hooks 规则）
  const internalTableRef: any = useRef<any>()
  // 如果外部传入了 ref，使用外部的；否则使用内部的
  const tableRef = props.tableRef || internalTableRef
  const actionButtons = props.actionButtons?.length ? props.actionButtons : []
  const isTableEditable = props.editable ? !props.isNotEditable : false
  const isTableAddable = props.editable ? !props.isNotAddable : false
  const isTableDeletable = props.editable ? !props.isNotDeletable : false
  const addRowButtonTooltip = 'Add'
  const addRowButtonName = props.addRowButtonName || 'Add new '
  const [data, setData] = useState(props.initData || [])
  const [column, setColumn] = useState(props.column)

  useEffect(() => {
    let isCancelled = false
    if (props.initData) {
      if (!isCancelled) {
        setData(props.initData)
      }
    }
    return () => {
      isCancelled = true
    }
  }, [props.initData])

  return (
    <div className={classes.tableContainer}>
      <MaterialTable
        tableRef={tableRef}
        title={props.title}
        columns={column}
        data={data}
        options={{
          toolbarButtonAlignment: 'left',
          pageSizeOptions: props.defaultPageSizeOptions || [5, 10, 30, 50, 100],
          pageSize: props.defaultPageSize || 100,
          exportButton: props.isExportable?true:false,
          emptyRowsWhenPaging: false,
          filtering: true,
          debounceInterval:500,          
          actionsColumnIndex: -1,
          addRowPosition: 'first',
          headerStyle: {
            fontSize: '1rem',
            color: '#1A3C34',
            fontWeight: 600,
          },
          doubleHorizontalScroll: true,
          draggable: true,
          selection: props.isEnableSelect,
          paginationType: 'stepped',
          selectionProps:props.selectionProps,
          rowStyle: (rowData) => {
            const baseStyle = props.rowStyle ? props.rowStyle(rowData) : {}
            return {
              ...baseStyle,
              fontSize: '0.9rem',
            }
          },
          columnsButton:props.columnsButton,
          sorting: props.disableSorting !== true, // 默认启用排序，如果 disableSorting 为 true 则禁用
        }}
        onSelectionChange={(rows) => {
          props.onSelectionChange(rows)
        }}
        onFilterChange={() => {
          if (props.onFilterChange)
            props.onFilterChange(tableRef)
        }}
        onSearchChange ={() => {
          if (props.onSearchChange )
            props.onSearchChange (tableRef)
        }}        
        onChangePage={() => {
          window.scrollTo(0, 0);
        }}
        localization={{
          header: {
            actions: props.editable ? 'Actions' : ''
          },
          body: {
            addTooltip: addRowButtonTooltip
          }
        }}
        actions={actionButtons}
        components={{
          Action: (propsAction: any) => {
            const onBnClick = (event: any) => propsAction.action.onClick(event, propsAction.data)
            const bnProps: any = {onClick: (event: any) => onBnClick(event)}
            // Override action buttons
            for (let i = 0; i < actionButtons.length; i++) {
              if (propsAction.action.tooltip === actionButtons[i].tooltip) {
                const restProps = {...bnProps, [actionButtons[i].icon || 'block']: true}
                return <CommonButton name={actionButtons[i].tooltip} restProps={restProps} />
              }
            }
            // Override "Add Row Button"
            if (propsAction.action.tooltip === addRowButtonTooltip) {
              const addBnProps: any = {...bnProps}
              if (props.onAddClick) {
                addBnProps.onClick = (event: any) => props.onAddClick(event)
              }
              const state = tableRef.current?.state
              if (!isTableAddable || !!state?.showAddRow || !!state?.lastEditingRow) {
                addBnProps.style = {display: 'none'}
              }
              return <CommonButton name={addRowButtonName} restProps={addBnProps} />
            }
            return <MTableAction {...propsAction}/>
          },
          OverlayLoading: () => null,
          Container: props => <Paper {...props} elevation={0} style={{border: '1px solid #e8f5e9', borderRadius: '12px'}}/>,
          Toolbar: toolbarProps => {
            return (
              <div>
                <MTableToolbar {...toolbarProps} />
                {props.headerFields && (
                  <div className={classes.headerFields}>
                    {props.headerFields.map((data: any, key: any) => {
                      return (
                        <div key={key}>
                          {data.click ? (
                            <a onClick={() => data.click(data.name)}>{data.name}</a>
                          ) : (
                            data.name
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }
        }}
        editable={{
          isEditHidden: _ => !isTableEditable,
          isDeleteHidden: _ => !isTableDeletable,
          isEditable: _ => isTableEditable,
          isDeletable: _ => isTableDeletable,
          onRowAdd: (newData) => new Promise((resolve, reject) => {
            // Manual validation check before proceeding using stored validation functions
            // Use column state variable instead of tableRef.current?.columns
            const columnsForValidation = column || []
            const errorMessages: string[] = []
            let hasValidationFn = false

            // Debug: log columns to check if _validationFn exists
            console.log('Columns for validation:', columnsForValidation.map((c: any) => ({ field: c.field })))
            console.log('New data for validation:', newData)

            for (const col of columnsForValidation) {
              const field = col.field || (typeof col === 'string' ? col : '')
              if (field) {
                const validationFn = getValidationFunction(field)
                if (validationFn) {
                  hasValidationFn = true
                  const validationResult = validationFn(newData)
                  console.log(`Validation for ${field}:`, validationResult)

                  if (validationResult !== true) {
                    const errorMessage = validationResult?.helperText || `${field} validation failed`
                    errorMessages.push(errorMessage)
                    console.log('Validation failed:', errorMessage)
                  }
                }
              }
            }

            if (!hasValidationFn) {
              console.warn('No validation functions found for columns')
            }

            if (errorMessages.length > 0) {
              console.log('Showing validation errors:', errorMessages)
              SweetAlertService.validationError(errorMessages)
              reject('Validation failed')
              return
            }

            if (!props.notAllowUpdate) {
              props.updateData([...data, newData], newData, 'create')
                .then((_: any) => {
                  resolve('')
                })
                .catch((error: any) => {
                  // Display validation error feedback
                  if (error?.errorFields || error?.message) {
                    SweetAlertService.validationError('Please fix validation errors before saving')
                  }
                  reject('')
                })
            } else {
              if (!data) {
                props.setTableData([newData])
              } else {
                props.setTableData([...data, newData])
              }
              resolve('')
            }
          }),
          onRowDelete: (oldData: any) => new Promise((resolve, reject) => {
            const dataDelete = [...data]
            const index = oldData.tableData.id
            dataDelete.splice(index, 1)
            if (!props.notAllowUpdate) {
              props.updateData([...dataDelete], oldData, 'delete')
                .then((_: any) => {
                  setData([...dataDelete])
                  resolve('')
                })
                .catch((_: any) => {
                  reject('')
                })
            } else {
              props.setTableData(dataDelete)
              resolve('')
            }
          }),
          onRowUpdate: async (newData, oldData) => new Promise((resolve, reject) => {
            const isNothingChange = compareTwoObjs({ ...newData, tableData: null }, { ...oldData, tableData: null })
            if (isNothingChange) {
              reject('')
            } else {
              // Manual validation check before proceeding using stored validation functions
              // Use column state variable instead of tableRef.current?.columns
              const columnsForValidation = column || []
              const errorMessages: string[] = []
              let hasValidationFn = false

              for (const col of columnsForValidation) {
                const field = col.field || (typeof col === 'string' ? col : '')
                if (field) {
                  const validationFn = getValidationFunction(field)
                  if (validationFn) {
                    hasValidationFn = true
                    const validationResult = validationFn(newData)
                    console.log(`Validation for ${field}:`, validationResult)

                    if (validationResult !== true) {
                      const errorMessage = validationResult?.helperText || `${field} validation failed`
                      errorMessages.push(errorMessage)
                      console.log('Validation failed:', errorMessage)
                    }
                  }
                }
              }

              if (!hasValidationFn) {
                console.warn('No validation functions found for columns')
              }

              if (errorMessages.length > 0) {
                console.log('Showing validation errors:', errorMessages)
                SweetAlertService.validationError(errorMessages)
                reject('Validation failed')
                return
              }

              const dataUpdate = [...data]
              const index = oldData.tableData.id
              dataUpdate[index] = newData
              if (!props.notAllowUpdate) {
                props.updateData([...dataUpdate], newData, 'update')
                  .then((_: any) => {
                    setData([...dataUpdate])
                    resolve('')
                  })
                  .catch((error: any) => {
                    // Display validation error feedback
                    if (error?.errorFields || error?.message) {
                      SweetAlertService.validationError('Please fix validation errors before saving')
                    }
                    reject('')
                  })
              } else {
                props.setTableData(dataUpdate)
                resolve('')
              }
            }
          }),
        }}
      />
    </div>
  )
}